<script setup>
import { onBeforeMount, onMounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useVehicleConfigurator } from "../../Composables/useVehicleConfigurator.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import FormSelectField from "../../Components/FormSelectField.vue"
import { chToKw } from "../../Utils/powerUtils.js"

const { httpErrorsHandler } = useHttpErrorsHandler()

const storageKey = "Shiftech_ChiptuningList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: chiptuning,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    search,
    loadData,
    changeSorting,
    updateFilters,
} = useDataManager({
    baseUrl: '/api/chiptuning',
    baseSearchUrl: '/api/search/chiptuning',
    defaultSortingField: 'updated_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        updated_at: 'desc',
        created_at: 'desc',
        vehicle_brand_name: 'asc',
        vehicle_model_name: 'asc',
        vehicle_version_name: 'asc',
        power_type: 'asc',
        horsepower: 'desc',
    },
    minSearchLength: 2,
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', slot: 'id', class: 'col-1' },
    { label: 'Type', slot: 'type', class: 'col-1' },
    { label: 'Constructeur', slot: 'brand', field: 'vehicle_brand_name', class: 'col-1', sortable: true },
    { label: 'Modèle', field: 'vehicle_model_name', class: 'col-1', sortable: true },
    { label: 'Version', field: 'vehicle_version_name', class: 'col-1', sortable: true },
    { label: 'Moteur', field: 'name', class: 'col-1' },
    { label: 'Carburant', slot: 'fuel', field: 'power_type', class: 'col-1', sortable: true },
    { label: 'Puissance - gain', slot: 'power', field: 'horsepower', class: 'col-1', sortable: true },
    { label: "Dispo", field: "stage1_available", class: "col-1 text-center", slot: "availability" },
    { label: 'Dernière MAJ', slot: 'updated_at', field: 'updated_at', class: 'col-1', sortable: true },
    { label: 'Création', slot: 'created_at', field: 'created_at', class: 'col-1', sortable: true },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
])

const errors = ref(null)
const filters = reactive({
    type: null,
    vehicle_brand_id: null,
    vehicle_model_id: null,
    vehicle_version_id: null,
})
const filtersApplied = ref(true)
const isInitialized = ref(false)

const {
    brands: carBrands,
    models: carModels,
    versions: carVersions,
    fetchBrands,
    fetchModels,
    fetchVersions,
} = useVehicleConfigurator(filters, {
    onError: httpErrorsHandler,
    includeEngines: false,
    includeAllOption: true,
    autoWatch: false,
    typeField: 'type',
})

onBeforeMount(async () => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        filtersApplied.value = savedSettings.filtersApplied ?? true

        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
            updateFilters({
                type: filters.type,
                vehicle_brand_id: filters.vehicle_brand_id,
                vehicle_model_id: filters.vehicle_model_id,
                vehicle_version_id: filters.vehicle_version_id,
            })

            if (filters.type !== null) {
                await fetchBrands()
            }
            if (filters.vehicle_brand_id !== null) {
                await fetchModels()
            }
            if (filters.vehicle_model_id !== null) {
                await fetchVersions()
            }
        }
    }

    loadData()
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, filtersApplied, filters],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newFiltersApplied]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filtersApplied: newFiltersApplied,
            filters: { ...filters },
        })
    },
    { deep: true }
)

watch(
    () => [filters.type, filters.vehicle_brand_id, filters.vehicle_model_id, filters.vehicle_version_id],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

watch(
    () => filters.type,
    async (newValue, oldValue) => {
        if (isInitialized.value && newValue !== oldValue) {
            filters.vehicle_brand_id = null
            filters.vehicle_model_id = null
            filters.vehicle_version_id = null
            await fetchBrands()
        }
    },
    { deep: true }
)

watch(
    () => filters.vehicle_brand_id,
    async (newValue, oldValue) => {
        if (isInitialized.value && newValue !== oldValue) {
            filters.vehicle_model_id = null
            filters.vehicle_version_id = null
            await fetchModels()
        }
    },
    { deep: true }
)

watch(
    () => filters.vehicle_model_id,
    async (newValue, oldValue) => {
        if (isInitialized.value && newValue !== oldValue) {
            filters.vehicle_version_id = null
            await fetchVersions()
        }
    },
    { deep: true }
)

function applyFilter() {
    updateFilters({
        type: filters.type,
        vehicle_brand_id: filters.vehicle_brand_id,
        vehicle_model_id: filters.vehicle_model_id,
        vehicle_version_id: filters.vehicle_version_id,
    })
    filtersApplied.value = true
    loadData()
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Tous les moteurs</h5>
                    </div>
                </div>

                <!-- Search -->
                <div class="card-body border border-dashed border-end-0 border-start-0">
                    <div class="row g-3 justify-content-between">
                        <div class="col-xxl-5 col-sm-9">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchChiptuning">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-xxl-1 col-sm-3">
                            <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom"></i>
                                Filtrer
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="card-body">
                    <div class="row g-3">
                        <form-select-field
                            name="filters.type"
                            label="Type"
                            :label-hidden="true"
                            @update:field="filters.type = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.type"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les types"
                            :liste-options="[
                                { text: 'Tous les types', value: null },
                                { text: 'Agricole', value: 'agricultural' },
                                { text: 'ATV', value: 'atv' },
                                { text: 'Camion', value: 'truck' },
                                { text: 'Jetski', value: 'jetski' },
                                { text: 'Moto', value: 'moto' },
                                { text: 'Voiture', value: 'car' },
                            ]"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.vehicle_brand_id"
                            label="Marques"
                            :label-hidden="true"
                            @update:field="filters.vehicle_brand_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.vehicle_brand_id"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            :placeholder="filters.type === null ? 'Sélectionnez un type' : 'Toutes les marques'"
                            :liste-options="carBrands"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.vehicle_model_id"
                            label="Modèles"
                            :label-hidden="true"
                            @update:field="filters.vehicle_model_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.vehicle_model_id"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            :placeholder="filters.vehicle_brand_id === null ? 'Sélectionnez une marque' : 'Tous les modèles'"
                            :liste-options="carModels"
                            margin="mb-0"
                        />
                        <form-select-field
                            name="filters.vehicle_version_id"
                            label="Versions"
                            :label-hidden="true"
                            @update:field="filters.vehicle_version_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.vehicle_version_id"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            :placeholder="filters.vehicle_model_id === null ? 'Sélectionnez un modèle' : 'Toutes les versions'"
                            :liste-options="carVersions"
                            margin="mb-0"
                        />
                    </div>
                </div>

                <!-- Table -->
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="chiptuning"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @page-changed="loadData"
                        @sort-changed="changeSorting"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-bold">#{{ item.data.id_api }}</span>
                        </template>

                        <!-- Type -->
                        <template #type="{ item }">
                            {{ item.data.vehicle_brand.data.type_label }}
                        </template>

                        <!-- Brand -->
                        <template #brand="{ item }">
                            <div class="d-flex align-items-center">
                                <div class="flex-shrink-0">
                                    <img :src="item.data?.vehicle_brand?.data.logo" alt="" class="avatar-xs rounded">
                                </div>
                                <div class="flex-grow-1 ms-2 name">
                                    {{ item.data?.vehicle_brand_name }}
                                </div>
                            </div>
                        </template>

                        <!-- Fuel -->
                        <template #fuel="{ item }">
                            {{ item.data.power_type_label }}
                        </template>

                        <!-- Power -->
                        <template #power="{ item }">
                            {{ item.data.horsepower }}<span class="text-muted"> ch</span> <span class="text-muted">({{ chToKw(item.data.horsepower) }} kW)</span> - {{ item.data.torque }}<span class="text-muted"> Nm</span>
                        </template>

                        <!--Availability badges-->
                        <template #availability="{ item }">
                            <div class="d-flex gap-1 justify-content-center">
                                <span class="badge fs-11 d-inline-flex align-items-center justify-content-center" style="width: 22px; height: 22px;" :class="item.data.stage1_available ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'" title="Stage 1">
                                    1
                                </span>
                                <span class="badge fs-11 d-inline-flex align-items-center justify-content-center" style="width: 22px; height: 22px;" :class="item.data.e85_available ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'" title="E85">
                                    E
                                </span>
                                <span class="badge fs-11 d-inline-flex align-items-center justify-content-center" style="width: 22px; height: 22px;" :class="item.data.e85plus_available ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'" title="E85+">
                                    E+
                                </span>
                            </div>
                        </template>

                        <!-- Updated at -->
                        <template #updated_at="{ item }">
                            {{ item.data.updated_at }}
                        </template>

                        <!-- Created at -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at }}
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'chiptuning.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>
                    </data-table>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
